import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { IndexQueryStringType, toFirstUppercase } from '@atmi/core/utils';

import {firstValueFrom, from} from 'rxjs';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, SwalService, UIExportService } from '@atmi/core';
import { CardD, CardDFilter, CardDFiltersMeta } from '@atmi/core/models';
import { SettingsApiService } from '@atmi/core/services/settings-api.service';
import { CardDFiltersComponent } from '@atmi/shared/filters/card-d-filters/card-d-filters.component';
import { first } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-cards-d',
  styleUrls: ['./cards-d.component.scss'],
  templateUrl: './cards-d.component.html',
})
export class CardsDComponent extends BaseTableFilteredComponent<CardD, CardDFilter> implements UiTableComponent<CardD> {
  readonly MobileFiltersComponent = CardDFiltersComponent;

  readonly fields: TableField<CardD>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'SGT.SGTT',
      sortable: true,
    },
    {
      context: (x) => x.MTC.map((mtc) => toFirstUppercase(mtc)).join(', '),
      header: this.translate.instant('Materia e Tecnica'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'MTC',
    },
    {
      context: (x) => `H: ${x.MIS.MISA || '-'} L: ${x.MIS.MISL || '-'} ${x.MIS.MISU}`,
      header: this.translate.instant('Dimensioni'),
      key: 'MIS',
    },
    {
      context: (x) => x.DT.length ? x.DT[0].DTS.DTSI : '-',
      header: this.translate.instant('Anno'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'DT.DTS.DTSI',
      sortable: true,
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  currentFilters: IndexQueryStringType = {};

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    sort = sort && sort.length > 0 ? sort : [{ field: 'SGT.SGTT', type: 'asc' }];
    this.currentFilters = { sort, filter };
    return this.cardsApi.indexD({ sort, page, limit, filter });
  });

  readonly contextMenus: Array<TableContextMenu<CardD>> = [];

  readonly isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className === 'BeneD');

  defaultLimit = 300;
  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly roleService: RoleService,
    private readonly settingsApi: SettingsApiService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, CardDFiltersMeta, swalService, 'd/manager');

    if (this.roleService.hasCapability('BeneD', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else if (this.roleService.hasCapability('BeneD', ['read', 'read+all'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Visualizza'),
      });
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('BeneD', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.cardsApi.removeD(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = [new Promise(() => ids.map((id) => firstValueFrom(this.cardsApi.removeD(id))))];

    return from(Promise.all(deleteApis));
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'BeneD');
    if (success) {
      this.selectedItems = [];
    }
  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string, format?: string) => {
        const selection = this.selectedItemIds.length ? this.selectedItemIds : undefined;
        const blob = await firstValueFrom(this.cardsApi.exportList('d', template, format, this.currentFilters, selection).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'BeneD',
      `beni-d`,
    );
  }
}
